import { Action, action, Thunk, thunk, ThunkOn, thunkOn, State, Store } from 'easy-peasy';
import { StoreState } from './store';
import { PlayerStatus } from '../typings';
import { ChromecastUtils } from '../utils/chromecast';

export interface ChromecastState {
  enabled: boolean;
  casting: boolean;
  castContext: cast.framework.CastContext | null;
  castState: cast.framework.CastState | null;
  castSession: cast.framework.CastSession | null;
  castMediaInfo: any | null;
  remotePlayer: cast.framework.RemotePlayer | null;
  remotePlayerController: cast.framework.RemotePlayerController | null;
  customMessagesHandler: ChromecastUtils.CustomMessagesHandler | null;
  videoSwitching: boolean;
  audioSwitching: boolean;

  setEnabled: Action<ChromecastState, boolean>;
  setCasting: Action<ChromecastState, boolean>;
  setCastContext: Action<ChromecastState, cast.framework.CastContext | null>;
  setCastState: Action<ChromecastState, cast.framework.CastState | null>;
  setCastSession: Action<ChromecastState, cast.framework.CastSession | null>;
  setCastMediaInfo: Action<ChromecastState, any | null>;
  setRemotePlayer: Action<ChromecastState, cast.framework.RemotePlayer | null>;
  setRemotePlayerController: Action<ChromecastState, cast.framework.RemotePlayerController | null>;
  setCustomMessagesHandler: Action<ChromecastState, ChromecastUtils.CustomMessagesHandler | null>;
  setVideoSwitching: Action<ChromecastState, boolean>;
  setAudioSwitching: Action<ChromecastState, boolean>;
  initialize: Thunk<ChromecastState, Store<StoreState>, any, StoreState>;
  reset: Thunk<ChromecastState, void, any, StoreState>;
  loadMedia: Thunk<ChromecastState, void, any, StoreState>;
  playOrPause: Thunk<ChromecastState, void, any, StoreState>;
  stop: Thunk<ChromecastState, void, any, StoreState>;
  seek: Thunk<ChromecastState, number, any, StoreState>;
  setVolume: Thunk<ChromecastState, number, any, StoreState>;
  muteOrUnmute: Thunk<ChromecastState, void, any, StoreState>;
  setAudioTrack: Thunk<ChromecastState, void, any, StoreState>;
  setSubtitleTrack: Thunk<ChromecastState, void, any, StoreState>;
  pauseLocalPlayback: Thunk<ChromecastState, void, any, StoreState>;
  resumeLocalPlayback: Thunk<ChromecastState, void, any, StoreState>;
  addChromecastListeners: Thunk<ChromecastState, void, any, StoreState>;
  removeChromecastListeners: Thunk<ChromecastState, void, any, StoreState>;
  onChromecastEvent: Thunk<ChromecastState, cast.framework.RemotePlayerChangedEvent, any, StoreState>;
  onChromecastSessionChanged: Thunk<ChromecastState, cast.framework.SessionStateEventData, void, StoreState>;
  onChromecastTimeChanged: Thunk<ChromecastState, cast.framework.RemotePlayerChangedEvent, any, StoreState>;
  onChromecastVolumeChanged: Thunk<ChromecastState, cast.framework.RemotePlayerChangedEvent, any, StoreState>;
  onChromecastMuteSoundChanged: Thunk<ChromecastState, cast.framework.RemotePlayerChangedEvent, any, StoreState>;
  onChromecastMediaInfoChanged: Thunk<ChromecastState, cast.framework.RemotePlayerChangedEvent, any, StoreState>;
  onChromecastStateChanged: Thunk<ChromecastState, cast.framework.RemotePlayerChangedEvent, any, StoreState>;
  onLocalPlayerSeek: ThunkOn<ChromecastState, void, StoreState>;
  onPlayerDestroy: ThunkOn<ChromecastState, void, StoreState>;
  onAudioTrackChanged: ThunkOn<ChromecastState, void, StoreState>;
  onSubtitleTrackChanged: ThunkOn<ChromecastState, void, StoreState>;
  onSubtitleSettingsChanged: ThunkOn<ChromecastState, void, StoreState>;
  onVideoSwitched: ThunkOn<ChromecastState, void, StoreState>;
}

const initialState: State<ChromecastState> = {
  enabled: false,
  casting: false,
  castContext: null,
  castState: null,
  castSession: null,
  castMediaInfo: null,
  remotePlayer: null,
  remotePlayerController: null,
  customMessagesHandler: null,
  videoSwitching: false,
  audioSwitching: false,
};

export const chromecastState: ChromecastState = {
  /*================
   * State      
   ================*/
  ...initialState,

  /*================
   * Actions      
   ================*/

  setEnabled: action((state, enabled) => {
    state.enabled = enabled;
  }),

  setCasting: action((state, active) => {
    state.casting = active;
  }),

  setCastContext: action((state, castContext) => {
    state.castContext = castContext;
  }),

  setCastState: action((state, castState) => {
    const { CONNECTING, CONNECTED } = cast.framework.CastState;

    state.castState = castState;

    // Update casting flag
    if (castState === CONNECTING || castState === CONNECTED) {
      state.casting = true;
    } else {
      state.casting = false;
    }
  }),

  setCastSession: action((state, castSession) => {
    state.castSession = castSession;
  }),

  setCastMediaInfo: action((state, castMediaInfo) => {
    state.castMediaInfo = castMediaInfo;
  }),

  setRemotePlayer: action((state, remotePlayer) => {
    state.remotePlayer = remotePlayer;
  }),

  setRemotePlayerController: action((state, remotePlayerController) => {
    state.remotePlayerController = remotePlayerController;
  }),

  setCustomMessagesHandler: action((state, customMessagesHandler) => {
    state.customMessagesHandler = customMessagesHandler;
  }),

  setVideoSwitching: action((state, videoSwitching) => {
    state.videoSwitching = videoSwitching;
  }),

  setAudioSwitching: action((state, audioSwitching) => {
    state.audioSwitching = audioSwitching;
  }),

  /*================
   * Thunks      
   ================*/

  initialize: thunk(async (actions, store) => {
    const castContext = cast.framework.CastContext.getInstance();
    const castSession = castContext.getCurrentSession();

    actions.setEnabled(true);
    actions.setCastContext(castContext);
    actions.setCastSession(castSession);

    if (castSession) {
      const customMessagesHandler = new ChromecastUtils.CustomMessagesHandler(castSession, store);
      customMessagesHandler.subscribe();
      actions.setCustomMessagesHandler(customMessagesHandler);
    }
  }),

  reset: thunk((actions, __, { getState }) => {
    const state = getState();

    if (state.enabled) {
      actions.removeChromecastListeners();
      state.customMessagesHandler?.unsubscribe();
    }

    Object.assign(state, { ...initialState, enabled: state.enabled });
  }),

  loadMedia: thunk(async (actions, _, { getStoreState, getStoreActions }) => {
    let storeState = getStoreState();
    const storeActions = getStoreActions();
    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

    if (!castSession || !storeState.player.videoItem) return;

    storeActions.player.setStatus(PlayerStatus.Buffering);
    actions.setCastMediaInfo(null);

    // Reload video URLs
    await storeActions.videos.fetchData();

    storeState = getStoreState();

    let videoGuid = storeState.config.videoGuid!;

    const mediaInfo = new chrome.cast.media.MediaInfo(videoGuid, 'application/x-mpegURL');
    mediaInfo.textTrackStyle = ChromecastUtils.getTextTrackStyle(storeState);

    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    request.customData = await ChromecastUtils.getCustomData(storeState);
    request.currentTime = storeState.player.playbackCurrentTime;
    request.autoplay = true;

    actions.addChromecastListeners();

    await castSession.loadMedia(request);

    // If local volume has been modified, use it in chromecast.
    if (storeState.player.volume < 100) {
      castSession.setVolume(storeState.player.volume / 100);
    }

    if (storeState.player.muted) {
      castSession.setMute(storeState.player.muted);
    }
  }),

  playOrPause: thunk(() => {
    const remotePlayer = new cast.framework.RemotePlayer();
    const remotePlayerController = new cast.framework.RemotePlayerController(remotePlayer);
    remotePlayerController.playOrPause();
  }),

  stop: thunk(async () => {
    const remotePlayer = new cast.framework.RemotePlayer();
    const remotePlayerController = new cast.framework.RemotePlayerController(remotePlayer);
    remotePlayerController.stop();
  }),

  seek: thunk((_, currentTime) => {
    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

    if (!castSession) return;

    const remotePlayer = new cast.framework.RemotePlayer();
    const remotePlayerController = new cast.framework.RemotePlayerController(remotePlayer);
    remotePlayer.currentTime = currentTime;
    remotePlayerController.seek();
  }),

  setVolume: thunk((_, volumeLevel) => {
    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

    if (!castSession) return;

    castSession.setVolume(volumeLevel);
  }),

  muteOrUnmute: thunk(() => {
    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

    if (!castSession) return;

    const remotePlayer = new cast.framework.RemotePlayer();
    const remotePlayerController = new cast.framework.RemotePlayerController(remotePlayer);
    remotePlayerController.muteOrUnmute();
  }),

  setAudioTrack: thunk((actions, __, { getStoreState }) => {
    const storeState = getStoreState();
    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

    if (!castSession || !storeState.player.audioTrack) return;

    actions.setAudioSwitching(true);

    castSession.sendMessage(ChromecastUtils.CUSTOM_MESSAGES_CHANNEL, {
      type: ChromecastUtils.MessageType.SET_AUDIO_TRACK,
      audioTrackName: storeState.player.audioTrack.label,
    });
  }),

  setSubtitleTrack: thunk((_, __, { getStoreState }) => {
    const storeState = getStoreState();
    const castSession = cast.framework.CastContext.getInstance().getCurrentSession();

    if (!castSession) return;

    castSession.sendMessage(ChromecastUtils.CUSTOM_MESSAGES_CHANNEL, {
      type: ChromecastUtils.MessageType.SET_SUBTITLE_TRACK,
      subtitleTrackLanguage: storeState.subtitles.activeSubtitle?.language || '',
    });
  }),

  pauseLocalPlayback: thunk((_, __, { getStoreState, getStoreActions }) => {
    const { player } = getStoreState();
    player.videoEngine?.pause();
  }),

  resumeLocalPlayback: thunk((_, __, { getStoreState, getStoreActions }) => {
    const { player } = getStoreState();
    const storeActions = getStoreActions();

    /** Set local video at the current time. */
    if (player.status === PlayerStatus.Playing || player.status === PlayerStatus.Paused) {
      storeActions.player.seek(player.playbackCurrentTime);
    }

    /** Determine whether to load next video OR continue playing locally. */
    if (player.status === PlayerStatus.Playing || player.status === PlayerStatus.Buffering) {
      /** Detect whether cast ended playing by comparing the current and duration times. */
      const chromecastEnded = player.playbackCurrentTime > player.playbackDurationTime - 2;

      /** If cast playback ended, move to the next video */
      if (chromecastEnded) {
        storeActions.player.setPlaybackCurrentTime(0);
        storeActions.videos.closePlayer();
      } else {
        /** If cast playback didn't end, resume playback locally */
        storeActions.player.setStatus(PlayerStatus.Playing);
        storeActions.player.play();
      }
    }
  }),

  addChromecastListeners: thunk(actions => {
    actions.removeChromecastListeners();

    const castContext = cast.framework.CastContext.getInstance();
    const remotePlayer = new cast.framework.RemotePlayer();
    const remotePlayerController = new cast.framework.RemotePlayerController(remotePlayer);
    const { RemotePlayerEventType, CastContextEventType } = cast.framework;

    castContext.addEventListener(CastContextEventType.SESSION_STATE_CHANGED, actions.onChromecastSessionChanged);

    remotePlayerController.addEventListener(RemotePlayerEventType.ANY_CHANGE, actions.onChromecastEvent);

    remotePlayerController.addEventListener(
      RemotePlayerEventType.CURRENT_TIME_CHANGED,
      actions.onChromecastTimeChanged
    );

    remotePlayerController.addEventListener(
      RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
      actions.onChromecastVolumeChanged
    );

    remotePlayerController.addEventListener(
      RemotePlayerEventType.IS_MUTED_CHANGED,
      actions.onChromecastMuteSoundChanged
    );

    remotePlayerController.addEventListener(
      RemotePlayerEventType.MEDIA_INFO_CHANGED,
      actions.onChromecastMediaInfoChanged
    );

    remotePlayerController.addEventListener(
      RemotePlayerEventType.PLAYER_STATE_CHANGED,
      actions.onChromecastStateChanged
    );

    actions.setRemotePlayer(remotePlayer);
    actions.setRemotePlayerController(remotePlayerController);
  }),

  removeChromecastListeners: thunk((actions, _, { getState }) => {
    const { RemotePlayerEventType } = cast.framework;
    const { remotePlayerController } = getState();

    if (!remotePlayerController) return;

    remotePlayerController.removeEventListener(RemotePlayerEventType.ANY_CHANGE, actions.onChromecastEvent);

    remotePlayerController.removeEventListener(
      RemotePlayerEventType.CURRENT_TIME_CHANGED,
      actions.onChromecastTimeChanged
    );

    remotePlayerController.removeEventListener(
      RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
      actions.onChromecastVolumeChanged
    );

    remotePlayerController.removeEventListener(
      RemotePlayerEventType.IS_MUTED_CHANGED,
      actions.onChromecastMuteSoundChanged
    );

    remotePlayerController.removeEventListener(
      RemotePlayerEventType.PLAYER_STATE_CHANGED,
      actions.onChromecastStateChanged
    );

    remotePlayerController.removeEventListener(
      RemotePlayerEventType.MEDIA_INFO_CHANGED,
      actions.onChromecastMediaInfoChanged
    );
  }),

  onChromecastEvent: thunk((_, event) => {
    console.log(event);
  }),

  onChromecastSessionChanged: thunk((actions, event) => {
    if (event.sessionState === cast.framework.SessionState.SESSION_ENDED) {
      actions.resumeLocalPlayback();
      actions.reset();
    }
  }),

  onChromecastTimeChanged: thunk((_, event, { getState, getStoreActions }) => {
    const state = getState();
    const storeActions = getStoreActions();

    if (!state.castSession) return;

    /**
     * Do not update the local player currentTime if Chromecast session ends.
     * The currentTime is used when the Chromecast session ends to determine if
     * a new video has to be loaded. */
    if (event.value === 0) return;

    /**
     * Do not update the local player current time if Chromecast
     * is switching the audio track. */
    if (state.audioSwitching) return;

    storeActions.player.setPlaybackCurrentTime(event.value);
  }),

  onChromecastVolumeChanged: thunk((_, event, { getState, getStoreActions }) => {
    const state = getState();
    const storeActions = getStoreActions();

    if (!state.castSession) return;

    storeActions.player.setVolume(event.value * 100);
  }),

  onChromecastMuteSoundChanged: thunk((_, event, { getState, getStoreActions }) => {
    const state = getState();
    const storeActions = getStoreActions();

    if (!state.castSession) return;

    storeActions.player.setMuted(event.value);
  }),

  onChromecastMediaInfoChanged: thunk((actions, event, { getState, getStoreActions }) => {
    const state = getState();
    const storeActions = getStoreActions();
    const newCastMediaInfo = event.value;

    actions.setCastMediaInfo(newCastMediaInfo);

    /**
     * Set initial tracks when new cast media info is received
     * with valid data, as might be `null` when switching videos,
     * and has not been loaded for the current media already.
     **/
    const shouldSetInitialTracks = !!newCastMediaInfo && !state.castMediaInfo;

    if (shouldSetInitialTracks) {
      actions.setAudioTrack();
      actions.setSubtitleTrack();
    }

    // Set chromecast volume settings into local player.
    if (state.remotePlayer) {
      const { volumeLevel, isMuted } = state.remotePlayer;
      storeActions.player.setVolume(volumeLevel * 100);
      isMuted ? storeActions.player.mute() : storeActions.player.unmute();
    }
  }),

  onChromecastStateChanged: thunk((actions, event, { getState, getStoreActions }) => {
    const state = getState();
    const storeActions = getStoreActions();

    if (!state.castSession) return;

    switch (event.value) {
      case 'PLAYING':
        storeActions.player.setStatus(PlayerStatus.Playing);
        actions.pauseLocalPlayback();
        if (state.audioSwitching) actions.setAudioSwitching(false);
        break;
      case 'PAUSED':
        storeActions.player.setStatus(PlayerStatus.Paused);
        if (state.audioSwitching) actions.setAudioSwitching(false);
        break;
      case 'BUFFERING':
        storeActions.player.setStatus(PlayerStatus.Buffering);
        break;
      case 'IDLE':
        if (!state.castSession || state.videoSwitching || state.audioSwitching) return;
        actions.resumeLocalPlayback();
        break;
    }
  }),

  /*================
   * Listeners      
   ================*/

  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy.startType];
    },
    function handler(actions) {
      actions.reset();
    }
  ),

  onLocalPlayerSeek: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.seek, storeActions.player.fastForward, storeActions.player.rewind];
    },
    function handler(actions, _, { getState, getStoreState }) {
      const { castSession } = getState();
      const { player } = getStoreState();

      if (castSession && player.videoElement) {
        actions.seek(player.videoElement.currentTime);
      }
    }
  ),

  onAudioTrackChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setAudioTrack];
    },
    function handler(actions, _, { getState }) {
      const { castSession } = getState();

      if (castSession) {
        actions.setAudioTrack();
      }
    }
  ),

  onSubtitleTrackChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.subtitles.setActiveSubtitle];
    },
    function handler(actions, _, { getState }) {
      const { castSession } = getState();

      if (castSession) {
        actions.setSubtitleTrack();
      }
    }
  ),

  onSubtitleSettingsChanged: thunkOn(
    function observer(_, storeActions) {
      return [
        storeActions.subtitles.setSubtitleTextColor,
        storeActions.subtitles.setSubtitleTextSize,
        storeActions.subtitles.setSubtitleTextWeight,
      ];
    },
    function handler(actions, _, { getStoreState }) {
      const storeState = getStoreState();
      const { castSession } = storeState.chromecast;

      if (castSession) {
        const textTrackStyle = ChromecastUtils.getTextTrackStyle(storeState);

        castSession.getMediaSession()?.editTracksInfo(
          new chrome.cast.media.EditTracksInfoRequest([], textTrackStyle),
          function onSuccess() {
            actions.setSubtitleTrack();
          },
          function onError(error) {
            console.warn(error);
          }
        );
      }
    }
  ),

  /**
   * When the active video gets switched, load
   * the new media into Chromecast.
   */
  onVideoSwitched: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.videos.switchVideo];
    },
    async function handler(actions, _, { getStoreState, getStoreActions }) {
      const storeState = getStoreState();
      const storeActions = getStoreActions();

      if (storeState.chromecast.castSession) {
        actions.setVideoSwitching(true);
        storeActions.player.setPlaybackCurrentTime(storeState.player.playbackSwitchTime);
        await actions.loadMedia();
        actions.setVideoSwitching(false);
      }
    }
  ),
};
